import React, { useState, useEffect, useContext } from 'react';
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableSortLabel,
  TablePagination,
} from '@mui/material';
import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import Loading from '../components/commonComponents/Loading';
import { AuthContext } from '../components/Auth/Auth';

export default function Submissions() {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userProfiles, setUserProfiles] = useState({});
  const [userTeams, setUserTeams] = useState({});
  const [sortConfig, setSortConfig] = useState({});
  const [pagination, setPagination] = useState({});
  const { currentUser } = useContext(AuthContext);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // 1️⃣ Fetch submissions
        const submissionsSnapshot = await getDocs(collection(db, 'submissions'));
        const submissionsList = submissionsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setSubmissions(submissionsList);

        // 2️⃣ Fetch users
        const usersSnapshot = await getDocs(collection(db, 'users'));
        const usersList = usersSnapshot.docs.map(doc => doc.data());
        const profiles = {};
        usersList.forEach(user => {
          profiles[user.uid] = user.name;
        });
        setUserProfiles(profiles);

        // 3️⃣ Fetch enrollments (all documents)
        const enrollmentsSnapshot = await getDocs(collection(db, 'enrollments'));
        const teamsMap = {};

        enrollmentsSnapshot.docs.forEach(docSnap => {
          const data = docSnap.data();
          if (!data.teams) return;

          data.teams.forEach(team => {
            const teamName = team.name || 'Unknown Team';
            team.members.forEach(memberUid => {
              teamsMap[memberUid] = teamName;
            });
          });
        });

        setUserTeams(teamsMap);

      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) return <Loading />;

  // Group submissions by club -> project
  const groupedSubmissions = {};
  submissions.forEach(sub => {
    const club = sub.submissionData?.club_name || 'Unknown Club';
    const project = sub.submissionData?.project_name || 'Unknown Project';
    if (!groupedSubmissions[club]) groupedSubmissions[club] = {};
    if (!groupedSubmissions[club][project]) groupedSubmissions[club][project] = [];
    groupedSubmissions[club][project].push(sub);
  });

  const sortSubmissions = (array, orderBy = 'team', order = 'asc') => {
    return array.sort((a, b) => {
      let aVal = '';
      let bVal = '';

      if (orderBy === 'team') {
        aVal = userTeams[a.submissionData?.user] || '';
        bVal = userTeams[b.submissionData?.user] || '';
      } else if (orderBy === 'user') {
        aVal = userProfiles[a.submissionData?.user] || '';
        bVal = userProfiles[b.submissionData?.user] || '';
      }

      if (aVal < bVal) return order === 'asc' ? -1 : 1;
      if (aVal > bVal) return order === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const handleSort = (tableId, property) => {
    setSortConfig(prev => {
      const current = prev[tableId] || { orderBy: property, order: 'asc' };
      const isAsc = current.orderBy === property && current.order === 'asc';
      return { ...prev, [tableId]: { orderBy: property, order: isAsc ? 'desc' : 'asc' } };
    });
  };

  const handleChangePage = (tableId, newPage) => {
    setPagination(prev => ({
      ...prev,
      [tableId]: { ...(prev[tableId] || {}), page: newPage }
    }));
  };

  const handleChangeRowsPerPage = (tableId, newRows) => {
    setPagination(prev => ({
      ...prev,
      [tableId]: { page: 0, rowsPerPage: newRows }
    }));
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Submissions
      </Typography>

      {Object.entries(groupedSubmissions).map(([clubName, projects]) => (
        <div key={clubName} style={{ marginBottom: '2rem' }}>
          <Typography variant="h5" gutterBottom>
            Club: {clubName}
          </Typography>

          {Object.entries(projects).map(([projectName, submissionsList]) => {
            const tableId = `${clubName}-${projectName}`;
            const { orderBy = 'team', order = 'asc' } = sortConfig[tableId] || {};
            const { page = 0, rowsPerPage = 5 } = pagination[tableId] || {};

            const sortedSubmissions = sortSubmissions([...submissionsList], orderBy, order);
            const paginatedSubmissions = sortedSubmissions.slice(
              page * rowsPerPage,
              page * rowsPerPage + rowsPerPage
            );

            return (
              <div key={projectName} style={{ marginBottom: '1.5rem' }}>
                <Typography variant="h6" gutterBottom>
                  Project: {projectName}
                </Typography>

                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sortDirection={orderBy === 'team' ? order : false}>
                          <TableSortLabel
                            active={orderBy === 'team'}
                            direction={orderBy === 'team' ? order : 'asc'}
                            onClick={() => handleSort(tableId, 'team')}
                          >
                            Team
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={orderBy === 'user' ? order : false}>
                          <TableSortLabel
                            active={orderBy === 'user'}
                            direction={orderBy === 'user' ? order : 'asc'}
                            onClick={() => handleSort(tableId, 'user')}
                          >
                            Student
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>Submission Link</TableCell>
                        <TableCell>Message</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedSubmissions.map(sub => {
                        const userId = sub.submissionData?.user;
                        const userName = userProfiles[userId] || userId;
                        const teamName = userTeams[userId] || 'No Team';
                        return (
                          <TableRow key={sub.id}>
                            <TableCell>{teamName}</TableCell>
                            <TableCell>{userName}</TableCell>
                            <TableCell>
                              {sub.submissionData?.submission_link ? (
                                <a
                                  href={sub.submissionData.submission_link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  View Submission
                                </a>
                              ) : (
                                <span>No Submission File</span>
                              )}
                            </TableCell>
                            <TableCell>{sub.submissionData?.message || '-'}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>

                  <TablePagination
                    component="div"
                    count={submissionsList.length}
                    page={page}
                    onPageChange={(e, newPage) => handleChangePage(tableId, newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) =>
                      handleChangeRowsPerPage(tableId, parseInt(e.target.value, 10))
                    }
                    rowsPerPageOptions={[5, 10, 25]}
                  />
                </TableContainer>
              </div>
            );
          })}
        </div>
      ))}
    </Container>
  );
}
